import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, PauseIcon } from '@heroicons/react/24/solid';
import { COLORS, FONTS } from './theme';
import InkText from './InkText';
import { trackEvent } from '../../utils/analytics';

const EASE = [0.22, 0.61, 0.36, 1] as const;

const rv = (delay: number) => ({
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '0px 0px -8% 0px' },
  transition: { duration: 1, delay, ease: EASE },
});

// Fixed organic waveform pattern (seeded LCG so it's identical every render)
const BAR_COUNT = 52;
const BAR_HEIGHTS: number[] = (() => {
  const heights: number[] = [];
  let seed = 7;
  for (let i = 0; i < BAR_COUNT; i++) {
    seed = (seed * 16807) % 2147483647;
    const r = seed / 2147483647;
    const envelope = 0.45 + 0.55 * Math.sin((i / BAR_COUNT) * Math.PI);
    const h = 0.18 + 0.82 * (0.35 * r + 0.65 * Math.abs(Math.sin(i * 0.7 + r * 2))) * envelope;
    heights.push(h);
  }
  return heights;
})();

const SamplePlayer: React.FC = () => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      setIsPlaying(false);
      trackEvent('paathshala_audio_complete', { episode: 'ep1_the_great_hero' });
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      await audio.play();
      setIsPlaying(true);
      trackEvent('paathshala_audio_play', {
        episode: 'ep1_the_great_hero',
        time: Math.floor(audio.currentTime),
      });
    } catch {
      // Audio not loaded yet — graceful fail
    }
  }, []);

  // Hero's "Listen to a 2-min sample" CTA scrolls here and starts playback
  useEffect(() => {
    const onListen = () => {
      window.setTimeout(() => {
        const audio = audioRef.current;
        if (audio && audio.paused) play();
      }, 700);
    };
    window.addEventListener('paathshaala:listen', onListen);
    return () => window.removeEventListener('paathshaala:listen', onListen);
  }, [play]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      trackEvent('paathshala_audio_pause', {
        episode: 'ep1_the_great_hero',
        time: Math.floor(audio.currentTime),
      });
    } else {
      play();
    }
  };

  // Click-to-seek on the waveform
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const wave = waveformRef.current;
    if (!audio || !wave || !duration) return;
    const rect = wave.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = frac * duration;
    setCurrentTime(audio.currentTime);
  };

  const formatTime = (t: number) => {
    const mins = Math.floor(t / 60);
    const secs = Math.floor(t % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? currentTime / duration : 0;
  const playedCount = Math.floor(progress * BAR_COUNT);

  return (
    <section id="audio-sample" className="py-[86px]">
      <div className="w-full max-w-[640px] md:max-w-[760px] mx-auto px-[26px] md:px-10">
        <motion.h2
          {...rv(0)}
          className="font-bold text-center leading-[1.15] [text-wrap:balance]"
          style={{
            fontFamily: FONTS.heading,
            color: COLORS.nearBlack,
            fontSize: 'clamp(30px, 8vw, 42px)',
          }}
        >
          Hear it for yourself
        </motion.h2>

        <motion.p
          {...rv(0.1)}
          className="text-center text-[17px] mt-4 mx-auto mb-8 max-w-[48ch] leading-relaxed [text-wrap:pretty]"
          style={{ fontFamily: FONTS.body, color: COLORS.brown }}
        >
          Paathshaala isn&rsquo;t one show &mdash; it&rsquo;s a whole library
          of series across genres. Here&rsquo;s the one we&rsquo;d start you
          with:
        </motion.p>

        <motion.p
          {...rv(0.2)}
          className="text-center italic mb-6 mx-auto max-w-[34ch] leading-[1.4] [text-wrap:balance]"
          style={{
            fontFamily: FONTS.heading,
            color: COLORS.redInk,
            fontSize: 'clamp(20px, 5.4vw, 27px)',
          }}
        >
          A restless teenager. A wise Guru. The war inside your own head.
        </motion.p>

        <motion.p
          {...rv(0.3)}
          className="text-center text-[17px] mx-auto mb-10 max-w-[50ch] leading-relaxed [text-wrap:pretty]"
          style={{ fontFamily: FONTS.body, color: COLORS.brown }}
        >
          In this one, a skeptical kid presses a wise Guru on why Mahavira is
          called &lsquo;The Great Hero&rsquo; &mdash; and what that has to do
          with exam pressure, the fear of falling behind, and the thoughts
          that won&rsquo;t settle at night. Press play below.
        </motion.p>

        {/* Player — a precious object */}
        <motion.div
          {...rv(0.4)}
          className="relative rounded-md p-[30px_26px_28px] md:p-[38px_40px_34px]"
          style={{
            background: `linear-gradient(170deg, ${COLORS.parchmentDark}, ${COLORS.parchmentDarker})`,
            border: '1px solid rgba(168,137,62,0.55)',
            boxShadow:
              '0 24px 60px -20px rgba(26,15,10,0.45), inset 0 1px 0 rgba(255,244,214,0.45)',
          }}
        >
          {/* Inner manuscript rule */}
          <div
            aria-hidden="true"
            className="absolute inset-[7px] rounded-[3px] pointer-events-none"
            style={{ border: '1px solid rgba(168,137,62,0.3)' }}
          />

          <p
            className="text-center text-xs font-bold uppercase tracking-[0.28em] mb-1.5"
            style={{ fontFamily: FONTS.body, color: COLORS.goldLeafDark }}
          >
            Listen to a 2-minute sample
          </p>
          <p
            className="text-center font-bold text-[23px] mb-6"
            style={{ fontFamily: FONTS.heading, color: COLORS.nearBlack }}
          >
            Episode 1: The Great Hero
          </p>

          <audio
            ref={audioRef}
            src="/audio/ElevenLabs_Paathshaala_Intro_Project.mp3"
            preload="metadata"
          />

          <div className="flex items-center gap-[18px] relative">
            {/* Gold leaf play button */}
            <button
              onClick={togglePlay}
              className="flex-none w-[68px] h-[68px] md:w-[76px] md:h-[76px] rounded-full flex items-center justify-center cursor-pointer transition-transform duration-300 hover:scale-105 active:scale-[0.97]"
              style={{
                border: '1px solid rgba(26,15,10,0.3)',
                background: `radial-gradient(circle at 32% 28%, #D8BC74, ${COLORS.goldLeaf} 45%, ${COLORS.goldLeafDark})`,
                boxShadow:
                  '0 3px 0 rgba(26,15,10,0.25), 0 14px 30px -8px rgba(139,26,26,0.4), inset 0 1px 0 rgba(255,246,220,0.6)',
                color: COLORS.nearBlack,
              }}
              aria-label={isPlaying ? 'Pause sample' : 'Play 2-minute sample'}
            >
              {isPlaying ? (
                <PauseIcon className="w-7 h-7" />
              ) : (
                <PlayIcon className="w-7 h-7 ml-1" />
              )}
            </button>

            <div className="flex-1 min-w-0">
              {/* Vermillion waveform, click-to-seek */}
              <div
                ref={waveformRef}
                onClick={handleSeek}
                className="flex items-center gap-[2px] h-14 cursor-pointer"
                aria-hidden="true"
              >
                {BAR_HEIGHTS.map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 min-w-[2px] rounded-[1px] transition-opacity duration-200"
                    style={{
                      height: `${Math.round(h * 100)}%`,
                      backgroundColor: COLORS.redInk,
                      opacity: i < playedCount ? 0.95 : 0.28,
                    }}
                  />
                ))}
              </div>
              <div
                className="flex justify-between text-sm tracking-[0.06em] mt-2 tabular-nums"
                style={{ fontFamily: FONTS.body, color: COLORS.brown }}
              >
                <span>{formatTime(currentTime)}</span>
                <span>{duration > 0 ? formatTime(duration) : '2:00'}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pull quote */}
        <div className="mt-[52px] mx-auto max-w-[44ch]">
          <div
            aria-hidden="true"
            className="w-[7px] h-[7px] rotate-45 mx-auto mb-[22px]"
            style={{ backgroundColor: COLORS.goldLeaf }}
          />
          <InkText
            as="blockquote"
            text="“You cannot find your true direction while you are actively at war with your own mind.”"
            baseDelay={150}
            step={60}
            className="text-center italic leading-[1.5] [text-wrap:pretty]"
            style={{
              fontFamily: FONTS.heading,
              color: COLORS.redInk,
              fontSize: 'clamp(21px, 5.6vw, 26px)',
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default SamplePlayer;
